import type { Release } from '@src/dto/Release';
import { Smartphone, Trash2 } from 'lucide-react';
import type { ReactNode } from 'react';
import {
  getReleaseStatusDisplay,
  isReleaseWaitingToGoLive,
} from '../../utils/releaseStatusConfig';
import { formatShortDate } from '../../utils/stringHelpers';
import { Button } from '../Button';
import { DataTable } from '../common/DataTable';
import { StatusPill } from '../portal';

interface ReleaseStatusContext {
  publishedVersionCode?: number | null;
  storeStatus?: 'published' | 'unpublished' | null;
}

interface DashboardReleasesTableProps {
  releases: Release[];
  currentPage: number;
  showActionsColumn: boolean;
  isLoading?: boolean;
  statusContext?: ReleaseStatusContext;
  /** Staff who may trigger the Store publish see "Go Live Now" on waiting rows. */
  canPublish?: boolean;
  isPublishing?: boolean;
  onPublish?: () => void;
  onPageChange: (page: number) => void;
  onOpenRelease: (release: Release) => void;
  onRequestDeleteRelease: (release: Release) => void;
}

const releasesPerPage = 10;
const HEADER_CLASSES =
  'px-4 py-3 text-left text-xs font-normal leading-4 text-seeker-400 whitespace-nowrap';
const CELL_CLASSES = 'px-4 py-4 align-middle';
// DataTable applies these as inline widths (table-layout: fixed scales them to
// the table). Figma gives Status the widest column so "Waiting to go live" +
// "Go Live Now" fit on one line.
const COLUMN_SIZES = {
  version: 170,
  releaseDate: 130,
  status: 330,
  fileName: 180,
  uploadedBy: 140,
  actions: 70,
};

const getReleaseId = (release: Release) => release.id;

/**
 * Staggered entrance for a row's content. Rows are keyed by release id, so
 * this only plays when a release first appears (initial load, next page), not
 * when the list refetches.
 */

export function DashboardReleasesTable({
  releases,
  currentPage,
  showActionsColumn,
  isLoading = false,
  statusContext = {},
  canPublish = false,
  isPublishing = false,
  onPublish,
  onPageChange,
  onOpenRelease,
  onRequestDeleteRelease,
}: DashboardReleasesTableProps) {
  const totalPages = Math.ceil(releases.length / releasesPerPage);
  const startIndex = (currentPage - 1) * releasesPerPage;
  const endIndex = startIndex + releasesPerPage;
  const currentReleases = releases.slice(startIndex, endIndex);

  const renderVersion = (release: Release) => (
    <div className="flex min-w-0 items-center gap-2">
      <span
        className="truncate text-sm font-bold text-white"
        title={release.versionName || undefined}
      >
        {release.versionName || 'Unknown'}
      </span>
      {release.versionCode != null && (
        <span className="shrink-0 text-xs tabular-nums text-seeker-500">
          ({release.versionCode})
        </span>
      )}
      {release.source === 'Hubspot' && (
        <span className="shrink-0 whitespace-nowrap rounded bg-seeker-800 px-1.5 py-0.5 text-xs text-seeker-500">
          via cli
        </span>
      )}
    </div>
  );

  const renderGoLive = (release: Release) =>
    canPublish &&
    onPublish &&
    isReleaseWaitingToGoLive(release, statusContext) ? (
      <Button
        variant="primaryBlue"
        className="h-8 shrink-0 whitespace-nowrap px-4"
        disabled={isPublishing}
        onClick={(event) => {
          event.stopPropagation();
          onPublish();
        }}
      >
        {isPublishing ? 'Publishing…' : 'Go Live Now'}
      </Button>
    ) : null;

  const renderStatus = (release: Release) => {
    const statusDisplay = getReleaseStatusDisplay(release, statusContext);
    const StatusIcon = statusDisplay.icon;
    const reason = release.processingError || release.rejectionReason;

    return (
      <div className="min-w-0">
        <div className="flex items-center gap-4">
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
            <StatusIcon
              className={`shrink-0 pointer-events-none ${statusDisplay.iconClassName}`}
            />
            <span className={`text-sm ${statusDisplay.textColorClassName}`}>
              {statusDisplay.label}
            </span>
          </span>
          {renderGoLive(release)}
        </div>
        {reason && (
          <div className="mt-0.5 truncate text-xs text-rose-400" title={reason}>
            {reason}
          </div>
        )}
      </div>
    );
  };

  const renderFileName = (release: Release) => (
    <div className="flex min-w-0 items-center gap-2 text-white">
      <Smartphone className="size-4 shrink-0" />
      <span className="truncate text-sm" title={release.releaseFileName}>
        {release.releaseFileName}
      </span>
    </div>
  );

  const renderDelete = (release: Release) =>
    release.source === 'Portal' ? (
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onRequestDeleteRelease(release);
        }}
        className="inline-flex size-8 items-center justify-center rounded-lg text-rose-400 transition-colors duration-150 hover:bg-rose-500/10 hover:text-rose-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
        title="Delete release"
        aria-label={`Delete ${release.versionName || 'release'}`}
      >
        <Trash2 className="size-4" />
      </button>
    ) : null;

  const column = (
    key: keyof typeof COLUMN_SIZES,
    header: string,
    accessor: (release: Release) => ReactNode,
    extraHeaderClass = '',
  ) => ({
    key,
    header,
    size: COLUMN_SIZES[key],
    headerClassName: `${HEADER_CLASSES} ${extraHeaderClass}`,
    className: CELL_CLASSES,
    accessor,
  });

  const releaseColumns = [
    column('version', 'Version Name', renderVersion),
    column('releaseDate', 'Release Date', (release) => (
      <span className="whitespace-nowrap text-sm tabular-nums text-white">
        {formatShortDate(release.created) || '-'}
      </span>
    )),
    column('status', 'Status', renderStatus),
    column('fileName', 'File Name', renderFileName),
    column('uploadedBy', 'Uploaded by', (release) => (
      <span
        className="block truncate text-sm text-white"
        title={release.uploadedByName || undefined}
      >
        {release.uploadedByName || '-'}
      </span>
    )),
    ...(showActionsColumn
      ? [
          column(
            'actions',
            'Actions',
            (release) => (
              <div className="flex justify-end">{renderDelete(release)}</div>
            ),
            'text-right',
          ),
        ]
      : []),
  ];

  // Mobile: one card per release (the Figma only covers desktop).
  const renderMobileItem = (release: Release) => {
    const statusDisplay = getReleaseStatusDisplay(release, statusContext);
    const StatusIcon = statusDisplay.icon;
    return (
      <div className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-3">
          {renderVersion(release)}
          <StatusPill
            tone={statusDisplay.pillTone}
            icon={<StatusIcon className={statusDisplay.iconClassName} />}
          >
            {statusDisplay.label}
          </StatusPill>
        </div>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <dt className="text-xs text-seeker-400">Release Date</dt>
          <dt className="text-xs text-seeker-400">Uploaded by</dt>
          <dd className="tabular-nums text-white">
            {formatShortDate(release.created) || '-'}
          </dd>
          <dd
            className="truncate text-white"
            title={release.uploadedByName || undefined}
          >
            {release.uploadedByName || '-'}
          </dd>
        </dl>
        {renderFileName(release)}
        {(release.processingError || release.rejectionReason) && (
          <div className="text-xs text-rose-400">
            {release.processingError || release.rejectionReason}
          </div>
        )}
        {(renderGoLive(release) || showActionsColumn) && (
          <div className="flex items-center justify-between gap-3">
            {renderGoLive(release)}
            {showActionsColumn ? renderDelete(release) : null}
          </div>
        )}
      </div>
    );
  };

  const releaseRowClassName = (release: Release) =>
    release.source === 'Hubspot'
      ? 'cursor-default'
      : 'cursor-pointer hover:bg-seeker-800/40';

  return (
    <div className="flex flex-col gap-4">
      <DataTable
        data={currentReleases}
        columns={releaseColumns}
        isLoading={isLoading}
        skeletonRowCount={5}
        rowHeight={64}
        getRowId={getReleaseId}
        tableClassName="min-w-[760px]"
        headerRowClassName="border-b border-seeker-800"
        rowBaseClassName="border-b border-seeker-800 transition-colors duration-150"
        rowClassName={releaseRowClassName}
        onRowClick={onOpenRelease}
        renderMobileItem={renderMobileItem}
        animateRows
      />
      {totalPages > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm tabular-nums text-seeker-400">
            Showing {startIndex + 1} to {Math.min(endIndex, releases.length)} of{' '}
            {releases.length} releases
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outlineMuted"
              className="h-8 px-3"
              onClick={() => onPageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            {getVisiblePages(currentPage, totalPages).map((page, index) =>
              page === '...' ? (
                <span
                  key={`ellipsis-${index}`}
                  className="px-1 text-seeker-500"
                >
                  …
                </span>
              ) : (
                <Button
                  key={page}
                  variant={page === currentPage ? 'primary' : 'outlineMuted'}
                  className="h-8 min-w-8 px-3"
                  onClick={() => onPageChange(page)}
                >
                  {page}
                </Button>
              ),
            )}
            <Button
              variant="outlineMuted"
              className="h-8 px-3"
              onClick={() =>
                onPageChange(Math.min(totalPages, currentPage + 1))
              }
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function getVisiblePages(currentPage: number, totalPages: number) {
  const pages: Array<number | '...'> = [];
  const maxPagesToShow = 5;

  if (totalPages <= maxPagesToShow) {
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }
  } else {
    pages.push(1);

    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);

    if (start > 2) {
      pages.push('...');
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < totalPages - 1) {
      pages.push('...');
    }

    pages.push(totalPages);
  }

  return pages;
}
